/**
 * Created by mongoose on 7/11/17.
 */
import React from "react";
import PropTypes from "prop-types";
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";
import Column from "react-virtualized/dist/commonjs/Table/Column";
import WindowScroller from "react-virtualized/dist/commonjs/WindowScroller";
import SortDirection from "react-virtualized/dist/commonjs/Table/SortDirection";
import {
  actionsRenderer,
  defaultCellRenderer,
  RowRenderer,
  HeaderRowRenderer,
  SortableTable,
} from "./Renderers";
import { sortByProperty } from "./utils";
import "react-virtualized/styles.css";
import { FormattedMessage, defineMessages } from "react-intl";
import SortIndicator from "./SortIndicator";
import styled from "styled-components";
import ReactTooltip from "react-tooltip";
import { TooltipCustom } from "../../Common/WidgetRenderer";

const messages = defineMessages({
  actions: {
    id: "app.virtualizedTable.actions",
    description: "Actions",
    defaultMessage: "Actions",
  },
  clone: {
    id: "app.entityList.tooltip.duplicate",
    description: "Duplicate message",
    defaultMessage: "Duplicate",
  },
  delete: {
    id: "app.entityList.tooltip.delete",
    description: "Delete message",
    defaultMessage: "Delete",
  },
  drag: {
    id: "app.entityList.tooltip.reorder",
    description: "Reorder message",
    defaultMessage: "Reorder",
  },
  share: {
    id: "app.entityList.tooltip.view",
    description: "View message",
    defaultMessage: "View",
  },
  edit: {
    id: "app.entityList.tooltip.edit",
    description: "Edit message",
    defaultMessage: "Edit",
  },
});

const Header = styled.div`
  display: flex;
  ${props =>
    props.sortable &&
    `:hover {
    text-decoration: underline;
  }`}
`;

function defaultCellDataGetter({ dataKey, rowData }) {
  if (typeof rowData.get === "function") {
    return rowData.get(dataKey);
  } else {
    return rowData[dataKey];
  }
}

function passThroughCellRenderer(cellProps) {
  return customProps => defaultCellRenderer({ ...cellProps, ...customProps });
}

function passThroughActionsCellRenderer(cellProps) {
  return customProps => actionsRenderer({ ...cellProps, ...customProps });
}

const headerRenderer = ({ label, dataKey, sortBy, sortDirection, field }) => (
  <Header sortable={!!sortBy}>
    <div style={{ overflow: "hidden" }}>{label} </div>
    {sortBy === dataKey && (
      <div>
        <SortIndicator visible={sortBy === dataKey} sortDirection={sortDirection} />
      </div>
    )}
    {field.tooltip && <TooltipCustom text={field.tooltip} id={field.fieldName} />}
  </Header>
);

class VirtualizedTable extends React.PureComponent {
  constructor(props) {
    super();
    let sortBy = props.sortBy;
    if (typeof sortBy === "undefined") sortBy = "id";
    const sortDirection = props.sortDirection || SortDirection.ASC;
    const sortedList = this.sortList({
      sortBy,
      sortDirection,
      list: [...props.entities],
    });

    const visibleRows = Math.min(props.visibleNumRows || sortedList.length, sortedList.length);
    const headerRowHeight = 50;
    const rowHeight = 45;
    const tableHeight = headerRowHeight + visibleRows * rowHeight;

    this.state = {
      disableHeader: false,
      headerHeight: headerRowHeight,
      height: tableHeight,
      overscanRowCount: 20,
      rowHeight: rowHeight,
      rowCount: sortedList.length,
      scrollToIndex: undefined,
      sortBy,
      sortDirection,
      sortedList,
      useDynamicRowHeight: false,
    };

    this.sort = this.sort.bind(this);
    this.onSortEnd = this.onSortEnd.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.entities &&
      nextProps.entities.length >= 0 &&
      nextProps.entities !== this.props.entities
    ) {
      const sortBy = this.state.sortBy;
      const sortDirection = this.state.sortDirection;
      const sortedList = this.sortList({
        sortBy,
        sortDirection,
        list: [...nextProps.entities],
      });

      const visibleRows = Math.min(
        nextProps.visibleNumRows || sortedList.length,
        sortedList.length
      );
      const headerRowHeight = 50;
      const rowHeight = 45;
      const tableHeight = headerRowHeight + visibleRows * rowHeight;

      this.setState({
        sortedList,
        height: tableHeight,
        rowCount: sortedList.length,
      });
      // accessing wrapped instance
      // const instance = this.SortableTable.getWrappedInstance();
      // instance.Table.forceUpdateGrid();
      // instance.forceUpdate();
    }
  }

  sortList({ sortBy, sortDirection, list }) {
    return sortByProperty({ list, property: sortBy, sortDirection });
  }

  sort({ sortBy, sortDirection }) {
    const sortedList = this.sortList({
      list: this.props.entities,
      sortBy,
      sortDirection,
    });
    this.setState({ sortBy, sortDirection, sortedList });
  }

  onSortEnd({ oldIndex, newIndex }) {
    if (this.props.sortBy) {
      const field = this.props.fields.find(elem => elem.fieldName === this.props.sortBy);
      const entity = this.state.sortedList[oldIndex];
      const swappedEntity = this.state.sortedList[newIndex];
      this.props.onSort({
        field,
        entity,
        newIndex,
        oldIndex,
        swappedEntity,
      });
    } else if (this.props.reorder) {
      if (oldIndex <= newIndex) {
        newIndex = newIndex + 1;
      }
      const entityId = this.state.sortedList[oldIndex].id;
      const afterId = newIndex > 0 ? this.state.sortedList[newIndex - 1].id : null;
      this.props.onReorder({
        id: entityId,
        afterId,
      });
    }
  }

  calculateFieldWidth(sortIndicatorWidth, fieldWidth) {
    if (fieldWidth && fieldWidth > 40) {
      return fieldWidth;
    }
    return (fieldWidth || 40) + sortIndicatorWidth;
  }

  render() {
    const {
      disableHeader,
      headerHeight,
      overscanRowCount,
      rowHeight,
      rowCount,
      scrollToIndex,
      sortBy,
      sortDirection,
      sortedList,
    } = this.state;

    const {
      sortable,
      allowClone,
      fields,
      validation,
      renderers,
      onToggle,
      onDelete,
      onEdit,
      onClone,
      customActions,
      allowDelete,
      allowEdit,
      reorder,
      maxColumnWidth,
      horizontalScroll,
    } = this.props;
    const sortIndicatorWidth = sortable ? 0 : 0;

    // It's necessary render tooltips outside of the virtualized table
    const customsTooltips =
      customActions &&
      customActions.map(action => (
        <ReactTooltip id={action.tooltipId} effect="solid">
          {action.tooltipText}
        </ReactTooltip>
      ));
    const actionTooltip = (
      <>
        {allowClone && (
          <ReactTooltip id="clone" effect="solid">
            <FormattedMessage {...messages.clone} />
          </ReactTooltip>
        )}
        <ReactTooltip id="sharing" effect="solid">
          <FormattedMessage {...messages.share} />
        </ReactTooltip>
        {
          <ReactTooltip id="edit" effect="solid">
            <FormattedMessage {...messages.edit} />
          </ReactTooltip>
        }
        <ReactTooltip id="delete" effect="solid">
          <FormattedMessage {...messages.delete} />
        </ReactTooltip>
        {reorder && (
          <ReactTooltip id="drag" effect="solid">
            <FormattedMessage {...messages.drag} />
          </ReactTooltip>
        )}
        {customsTooltips}
      </>
    );

    return (
      <>
        {actionTooltip}
        <AutoSizer disableHeight>
          {({ width }) => {
            const usedWidth = horizontalScroll
              ? fields.reduce(
                  (p, f) => p + this.calculateFieldWidth(sortIndicatorWidth, f.width),
                  200
                )
              : width >= 1000
              ? width
              : 1500;

            return (
              <WindowScroller>
                {({ height, isScrolling, onChildScroll, scrollTop }) => (
                  <SortableTable
                    helperClass="witadmin-bs3 sorting"
                    ref={c => (this.SortableTable = c)}
                    disableHeader={disableHeader}
                    overscanRowCount={overscanRowCount}
                    scrollToIndex={scrollToIndex}
                    sort={this.sort}
                    sortBy={sortBy}
                    isScrolling={isScrolling}
                    scrollTop={scrollTop}
                    onChildScroll={onChildScroll}
                    sortDirection={sortDirection}
                    width={usedWidth}
                    autoHeight
                    height={height}
                    headerHeight={headerHeight}
                    rowHeight={rowHeight}
                    rowCount={rowCount}
                    rowGetter={({ index }) => sortedList[index]}
                    rowRenderer={RowRenderer}
                    rowStyle={{ width: usedWidth }}
                    headerRowRenderer={HeaderRowRenderer}
                    onSortEnd={this.onSortEnd}
                    useDragHandle>
                    {fields.map(field => (
                      <Column
                        key={field.fieldName}
                        label={field.displayName}
                        dataKey={field.fieldName}
                        headerRenderer={({ label, dataKey, sortBy, sortDirection }) =>
                          headerRenderer({ label, dataKey, sortBy, sortDirection, field })
                        }
                        cellRenderer={passThroughCellRenderer({
                          field,
                          validation: validation[field.fieldName],
                          entities: sortedList,
                          renderers: renderers,
                          onToggle,
                          onDelete,
                          onEdit,
                          customActions,
                          allowDelete,
                          allowEdit,
                          reorder,
                        })}
                        cellDataGetter={defaultCellDataGetter}
                        width={this.calculateFieldWidth(sortIndicatorWidth, field.width)}
                        maxWidth={maxColumnWidth}
                        disableSort={!sortable && !field.sortProperty}
                        flexGrow={1}
                      />
                    ))}
                    {(allowClone !== false ||
                      allowEdit !== false ||
                      allowDelete !== false ||
                      (customActions && customActions.length)) && (
                      <Column
                        disableSort
                        label={<FormattedMessage {...messages.actions} />}
                        dataKey="random"
                        cellRenderer={passThroughActionsCellRenderer({
                          entities: sortedList,
                          renderers: renderers,
                          onToggle,
                          onDelete,
                          onEdit,
                          onClone,
                          customActions,
                          sortable,
                          allowClone,
                          allowDelete,
                          allowEdit,
                          reorder,
                        })}
                        width={200}
                        flexGrow={1}
                      />
                    )}
                  </SortableTable>
                )}
              </WindowScroller>
            );
          }}
        </AutoSizer>
      </>
    );
  }
}

VirtualizedTable.propTypes = {
  horizontalScroll: PropTypes.bool,
};

VirtualizedTable.defaultProps = {
  horizontalScroll: false,
};

export default VirtualizedTable;
